import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * <kahi-cart-form> wraps a plain /cart/add form (single `id`+`quantity` or `items[]` bundles).
 * Without JS the form posts normally; with JS it adds via AJAX and dispatches Horizon's
 * CartLinesUpdateEvent, so the cart drawer, header count and cart items refresh themselves.
 */
class KahiCartForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#onSubmit);
    this.form?.addEventListener('change', this.#onChange);
    this.#watchSticky();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.form?.removeEventListener('change', this.#onChange);
    this.stickyObserver?.disconnect();
  }

  /** Shows the sticky bar once the main add-to-bag button has scrolled above the viewport. */
  #watchSticky() {
    const sticky = this.querySelector('[data-k-sticky]');
    const main = this.querySelector('[data-k-buy-main]');
    if (!sticky || !main) return;
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      sticky.toggleAttribute('data-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    this.stickyObserver.observe(main);
  }

  /** Subscribe & save: the selling plan is submitted only while "Subscribe" is chosen. */
  #onChange = (event) => {
    if (event.target.name !== 'purchase') return;
    const plan = this.form.querySelector('[name="selling_plan"]');
    if (plan) plan.disabled = event.target.value !== 'subscribe';
  };

  #onSubmit = async (event) => {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    if (button?.disabled) return;

    const body = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) body.set('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: this.#lines(body), promise: deferred.promise })
    );

    button?.setAttribute('aria-busy', 'true');
    try {
      const added = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body })).then((r) => r.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: Boolean(added.status), items: cart.items, sections: added.sections, source: 'kahi-cart-form' },
      });
      if (added.status) this.#announce(added.description || added.message);
    } catch (error) {
      deferred.reject(error);
      this.form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  /** @param {FormData} body */
  #lines(body) {
    if (body.has('id')) return [{ merchandiseId: String(body.get('id')), quantity: Number(body.get('quantity')) || 1 }];
    const lines = [];
    for (const [key, value] of body) {
      const match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (match) lines.push({ merchandiseId: String(value), quantity: Number(body.get(`items[${match[1]}][quantity]`)) || 1 });
    }
    return lines;
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[role="status"]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('kahi-cart-form')) customElements.define('kahi-cart-form', KahiCartForm);
